"use client";

import { c as _c } from "react/compiler-runtime";
import { ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ReactionSummary } from "@/domain/feature-announcement-feedback/feedback-reactions";
import { buildThreadItems } from "../lib/group-messages";
import { formatMessageTimestamp } from "../lib/thread-time";
import { FeedbackReactionBar } from "./feedback-reaction-bar";
export type FeedbackThreadAuthorType = "team" | "user";

/** An image attached to a thread message. url null = presign failed (placeholder tile). */
export interface FeedbackThreadAttachment {
  readonly id: string;
  readonly url: string | null;
  readonly fileName: string;
}

/**
 * The minimal message shape the thread renders. Deliberately has NO `author`
 * field: both the admin wire (which carries `author`) and the user wire (which
 * omits it via `S.omit("author")`) are structurally assignable, and the raw
 * author can never reach the DOM because the component never reads it.
 * `createdAt` is `unknown` — the lib normalises DateTime.Utc / ISO / number / Date.
 */
export interface FeedbackThreadMessage {
  readonly id: string;
  readonly authorType: FeedbackThreadAuthorType;
  readonly content: string;
  readonly createdAt: unknown;
  /** Optimistic-send bookkeeping; set by the admin wrapper only. */
  readonly clientKey?: string;
  readonly status?: "sending" | "failed";
  /** Images attached to this message (optional — most messages have none). */
  readonly attachments?: ReadonlyArray<FeedbackThreadAttachment>;
  /**
   * The reaction key for this bubble: a note id, or `null` for the original
   * submission. Omit it and `id` is used, which is correct for every real note
   * bubble (the admin surface passes wire messages straight through).
   *
   * It must be set EXPLICITLY wherever `id` is not a note id — the user side
   * prepends the original submission with a synthetic `original-${feedbackId}`,
   * so that bubble passes `noteId: null`. `undefined` and `null` mean different
   * things here, which is why the fallback below tests for `undefined`.
   */
  readonly noteId?: string | null;
  /** Aggregated per bubble. Absent means "this surface does not show reactions". */
  readonly reactions?: ReadonlyArray<ReactionSummary>;
  /**
   * The replying staffer's first name, resolved server-side for the user wire.
   * Read on the submitter side only, for team bubbles: "Tai · Cova". Absent or
   * null falls back to "Cova".
   */
  readonly authorFirstName?: string | null;
}
interface ThreadBaseProps {
  readonly messages: readonly FeedbackThreadMessage[];
  /** Insert "Today / Yesterday / 20 Jun" separators between local-day buckets. Default off. */
  readonly showDaySeparators?: boolean;
  /** Drop the repeated author label on consecutive same-author bubbles (<5 min). Default off. */
  readonly groupConsecutive?: boolean;
  /** Open a lightbox for a message's attachments. Omit to render non-clickable tiles. */
  readonly onImageClick?: (attachments: ReadonlyArray<FeedbackThreadAttachment>, index: number) => void;
  /**
   * Flip an emoji on a bubble. Lives on the BASE props, not one arm: the props
   * are a discriminated union, so an arm-level prop would reach one perspective
   * only. Omit to render reactions read-only.
   */
  readonly onToggleReaction?: (noteId: string | null, emoji: string) => void;
  readonly className?: string;
}
interface TeamThreadProps extends ThreadBaseProps {
  readonly perspective: "team";
  /** The submitter's real name, shown on their (left) bubbles so the admin knows
   *  who they're talking to. Required on the team side. */
  readonly submitterName: string;
  /** Retry a failed optimistic send (team side only). */
  readonly onRetry?: (clientKey: string) => void;
}
interface SubmitterThreadProps extends ThreadBaseProps {
  readonly perspective: "submitter";
  /** Forbidden: the user must never receive their own identity echoed back, and
   *  the team identity is already omitted server-side. Typing it `never` makes a
   *  leak a compile error — defense-in-depth behind the server-side S.omit. */
  readonly submitterName?: never;
  readonly onRetry?: never;
}
export type FeedbackThreadProps = TeamThreadProps | SubmitterThreadProps;

/**
 * The shared conversation bubble thread. The viewer's own messages render
 * brand-on-their-side. The `perspective` discriminant swaps which `authorType`
 * is "mine" and supplies the left-bubble label:
 *  - "submitter": user's own bubbles say "You" (right, brand); team bubbles say
 *    the replier's first name with a quiet "· Cova" marker, or "Cova" alone when
 *    the name is unknown (left).
 *  - "team": Cova's bubbles say "Cova" (right, brand); the submitter's say their
 *    real name (left).
 * Pure presentational — no data fetching, no feature imports.
 */
export function FeedbackThread(props) {
  const $ = _c(16);
  const {
    messages,
    perspective,
    showDaySeparators: t0,
    groupConsecutive: t1,
    className
  } = props;
  const showDaySeparators = t0 === undefined ? false : t0;
  const groupConsecutive = t1 === undefined ? false : t1;
  let t2;
  let t3;
  if ($[0] !== className || $[1] !== groupConsecutive || $[2] !== messages || $[3] !== perspective || $[4] !== props || $[5] !== showDaySeparators) {
    const items = buildThreadItems(messages, {
      showDaySeparators,
      groupConsecutive
    });
    if ($[8] !== className) {
      t2 = cn("space-y-3", className);
      $[8] = className;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    let t4;
    if ($[10] !== perspective || $[11] !== props) {
      t4 = item => {
        if (item.kind === "separator") {
          return <div key={item.id} className="flex items-center justify-center"><span className="rounded-full bg-black/4 px-2.5 py-0.5 text-[10px] font-medium text-gray-500">{item.label}</span></div>;
        }
        const m = item.message;
        const isMine = perspective === "team" ? m.authorType === "team" : m.authorType === "user";
        const replier = perspective === "submitter" && m.authorType === "team" ? m.authorFirstName ?? null : null;
        const label = perspective === "team" ? m.authorType === "team" ? "Cova" : props.submitterName : m.authorType === "user" ? "You" : replier ?? "Cova";
        const isSending = m.status === "sending";
        const isFailed = m.status === "failed";
        return <div key={m.clientKey ?? m.id} className={cn("group flex flex-col gap-1", isMine ? "items-end" : "items-start")}><div className={cn("max-w-[82%] rounded-2xl px-3 py-2 shadow-xs", isMine ? "bg-[#5651ff] text-white" : "border border-black/6 bg-white", isSending && "opacity-70")}>{!item.grouped && <p className={cn("mb-0.5 text-[11px] font-semibold", isMine ? "text-white/85" : "text-[#5651ff]")}>{label}{replier !== null && <span className="ml-1 font-medium text-gray-400">· Cova</span>}</p>}{m.content.trim().length > 0 && <p className="whitespace-pre-wrap text-sm">{m.content}</p>}{m.attachments && m.attachments.length > 0 && <div className={cn("flex flex-wrap gap-1.5", m.content.trim().length > 0 && "mt-1.5")}>{m.attachments.map((attachment, idx) => {
                const tile = attachment.url ? <img src={attachment.url} alt={attachment.fileName} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center"><ImageIcon className={cn("h-4 w-4", isMine ? "text-white/60" : "text-gray-400")} /></div>;
                const tileClasses = cn("h-16 w-16 overflow-hidden rounded-lg", isMine ? "bg-white/15" : "border border-black/6 bg-gray-50");
                return props.onImageClick && attachment.url ? <button key={attachment.id} type="button" aria-label={`View ${attachment.fileName}`} onClick={() => props.onImageClick?.(m.attachments, idx)} className={cn(tileClasses, "transition-all hover:opacity-90 hover:shadow-md active:scale-[0.97]")}>{tile}</button> : <div key={attachment.id} className={tileClasses}>{tile}</div>;
              })}</div>}<p className={cn("mt-1 text-[10px]", isMine ? "text-white/70" : "text-gray-400")}>{isSending ? "sending\u2026" : formatMessageTimestamp(m.createdAt)}</p>{isFailed && <div className={cn("mt-1 flex items-center gap-2 text-[10px]", isMine ? "text-white/90" : "text-red-500")}><span>Couldn't send.</span>{perspective === "team" && props.onRetry && m.clientKey && <button type="button" onClick={() => props.onRetry?.(m.clientKey)} className={cn("font-semibold underline underline-offset-2", isMine ? "text-white" : "text-red-600")}>Retry</button>}</div>}</div>{m.reactions !== undefined && !isSending && !isFailed && <FeedbackReactionBar reactions={m.reactions} align={isMine ? "right" : "left"} onToggle={props.onToggleReaction ? emoji => props.onToggleReaction?.(m.noteId !== undefined ? m.noteId : m.id, emoji) : undefined} />}</div>;
      };
      $[10] = perspective;
      $[11] = props;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    t3 = items.map(t4);
    $[0] = className;
    $[1] = groupConsecutive;
    $[2] = messages;
    $[3] = perspective;
    $[4] = props;
    $[5] = showDaySeparators;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[13] !== t2 || $[14] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}